import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  useGridState,
  useSearchApi,
  ButtonContainer,
  FieldSet,
  showError,
  FieldGroup,
  FGTextInput,
  FGMaskInput,
  FGCustomPanel,
  InlineButtonContainer,
  ButtonsBloc,
  DataTable
} from "nsitools-react";
import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router";

import {
  ContactApi,
  ContactLieuDoublonGridDto,
  ContactLieuDoublonSearch,
  ContactLieuDoublonSearchFromJSON,
  TuteurLieuDoublonGridDto
} from "../../../../../../api";
import { ERoutes } from "../../../../../../AppRouter";
import {
  ViewButton,
  EditButton,
  AddButton,
  UnordoredListColumn,
  SmallFormGenerator,
  FGWalterSelectInput,
  FGWalterDateMaskInput,
  CancelButton
} from "../../../../../../components";
import { useBeIdCardContext, useDialog } from "../../../../../../contexts";
import { useTl, useApiService } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface ILieuFormationContactDoublonsProps {
  setBaseData: (sObj: ContactLieuDoublonSearch) => void;
}

export const LieuFormationContactDoublons: React.FunctionComponent<ILieuFormationContactDoublonsProps> = ({
  setBaseData
}) => {
  const { t } = useTl();
  const dialogContext = useDialog();
  const history = useHistory();
  const { search: searchQ } = useLocation();
  const [nationalites, nLoading] = useReferential(a => a.referentialGetAllNationalite(), false, []);
  const api = useApiService(ContactApi);
  const { id } = useParams<{ id: string }>();
  const { foundRowsData } = useBeIdCardContext();
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [10],
    pageSize: 10,
    sortKeys: { nom: "ASC" }
  });
  const { totalCount } = tableState;

  const searchFunction = React.useCallback(
    (sObj?: ContactLieuDoublonSearch) => {
      sObj.idlieuFormation = +id;
      return api.contactSearchDoublonsLieu({ ContactLieuDoublonSearch: sObj });
    },
    [api, id]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: false
  });
  const add = React.useCallback(
    (fromPersonne: boolean) => {
      if (totalCount && totalCount > 0) {
        dialogContext.showDialog({
          message: t(fromPersonne ? ETLCodes.AddFormeConfirmMessage : ETLCodes.AddConfirmMessage, {
            forme: t(ETLCodes.Contact)
          }),
          onConfirmed: async () => {
            history.push(`${ERoutes.lieuFormation}/${id}/contacts/edit/-1${searchQ}`);
          }
        });
      } else {
        history.push(`${ERoutes.lieuFormation}/${id}/contacts/edit/-1${searchQ}`);
      }
    },
    [dialogContext, history, id, searchQ, t, totalCount]
  );

  const initialData = React.useMemo(() => {
    const onlyRegNatNb =
      !foundRowsData ||
      (!foundRowsData.nom && !foundRowsData.prenom && !foundRowsData.dateNaissance && foundRowsData.nationalNb);
    return ContactLieuDoublonSearchFromJSON({
      nom: foundRowsData?.nom,
      prenom: foundRowsData?.prenom,
      registreNational: onlyRegNatNb ? foundRowsData?.nationalNb : undefined,
      dateNaissance: foundRowsData?.dateNaissance
    });
  }, [foundRowsData]);

  React.useEffect(() => {
    if (foundRowsData) {
      search(initialData);
    }
  }, [foundRowsData, initialData, search]);

  const columns = React.useMemo(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: ContactLieuDoublonGridDto) => (
          <ButtonContainer>
            {row.isContact && !!row.idcontactLieu ? (
              <>
                <ViewButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.lieuFormation}/${id}/contacts/view/${row.idcontactLieu}`)}
                />
                <EditButton
                  minimal={true}
                  onClick={() => history.push(`${ERoutes.lieuFormation}/${id}/contacts/edit/${row.idcontactLieu}`)}
                />
              </>
            ) : (
              <AddButton
                tooltipText={t(row.isContact ? ETLCodes.CreerAPartirdeContact : ETLCodes.CreerAPartirdePersonne)}
                onClick={() => {
                  setBaseData({ ...row });
                  add(true);
                }}
              />
            )}
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom"
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom"
      },
      {
        header: () => t(ETLCodes.DateNaissance),
        fieldName: "dateNaissance"
      },
      {
        header: () => t(ETLCodes.NumeroNational),
        fieldName: "registreNational"
      },
      {
        header: () => t(ETLCodes.Adresse),
        fieldName: "adresse"
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      },
      {
        header: () => t(ETLCodes.Nationalite),
        fieldName: "nationalite"
      }
      // {
      //   header: () => t(ETLCodes.Forme),
      //   fieldName: "forme",
      //   render: (row: TuteurLieuDoublonGridDto) => (
      //     <UnordoredListColumn
      //       values={[
      //         { value: row.isApprenant, label: t(ETLCodes.Apprenant) },
      //         { value: row.isFormateur, label: t(ETLCodes.Formateur) },
      //         { value: row.isRepresentant, label: t(ETLCodes.Representant) },
      //         { value: row.isTuteur, label: t(ETLCodes.Tuteur) },
      //         { value: row.isChefEntreprise, label: t(ETLCodes.ChefEntreprise) },
      //         { value: row.isCollaborateur, label: t(ETLCodes.Collaborateur) },
      //         { value: row.isDelegueTutelle, label: t(ETLCodes.Referent) },
      //         { value: row.isConseillePedagogique, label: t(ETLCodes.ConseillerPedagogique) },
      //         { value: row.isContact, label: t(ETLCodes.Contact) }
      //       ]}
      //     />
      //   )
      // }
    ],
    [add, history, id, setBaseData, t]
  );

  const [firstSearch, setFirstSearch] = React.useState(false);

  return (
    <FieldSet collapsable={false} title={t(ETLCodes.Contacts)}>
      <SmallFormGenerator
        enableDirtyCheck={false}
        initialValues={initialData}
        onSubmit={c => {
          if ((c.nom && c.prenom) || c.registreNational) {
            search(c);
            setFirstSearch(true);
          } else {
            showError(t(ETLCodes.NoCriteriaFilled));
          }
        }}
        editMode
        hideButtons={true}
        onCancel={() => history.push(`${ERoutes.lieuFormation}/${id}/contacts`)}
      >
        <FieldGroup columns={2}>
          <FGTextInput name="nom" label={t(ETLCodes.Nom)} maxLength={100} forceCase="upper" />
          <FGWalterSelectInput
            name="nationalite"
            label={t(ETLCodes.Nationalite)}
            items={nationalites}
            loading={nLoading}
          />
          <FGTextInput name="prenom" label={t(ETLCodes.Prenom)} maxLength={100} />
          <FGMaskInput
            name="registreNational"
            label={t(ETLCodes.NumeroNational)}
            cleaveOptions={{ delimiters: [".", ".", "-", "."], blocks: [2, 2, 2, 3, 2], numericOnly: true }}
          />
          <FGWalterDateMaskInput name="dateNaissance" label={t(ETLCodes.DateNaissance)} />
        </FieldGroup>
        <FieldGroup>
          <FGCustomPanel>
            {ctx => (
              <InlineButtonContainer>
                <ButtonsBloc></ButtonsBloc>
                <ButtonsBloc>
                  <CancelButton
                    minimal={false}
                    onClick={() => history.push(`${ERoutes.lieuFormation}/${id}/contacts`)}
                  />
                  <Button icon={IconNames.SEARCH} type="submit" text={t(ETLCodes.Search)} intent={Intent.PRIMARY} />
                  <AddButton
                    onClick={() => {
                      setBaseData(ctx.formik.values);
                      add(false);
                    }}
                    text={t(ETLCodes.General_Add)}
                    intent={Intent.PRIMARY}
                    minimal={false}
                    disabled={!firstSearch}
                  />
                </ButtonsBloc>
              </InlineButtonContainer>
            )}
          </FGCustomPanel>
        </FieldGroup>
        <FGCustomPanel>
          {ctx => (
            <FieldSet title={t(ETLCodes.TableResults, { count: totalCount })}>
              <DataTable dateFormat="dd-MM-yyyy" tableState={tableState} loading={loading} columns={columns} />
            </FieldSet>
          )}
        </FGCustomPanel>
      </SmallFormGenerator>
    </FieldSet>
  );
};
